import useSWR from 'swr';
import { useState } from 'react';

import recipesService, { defaultSummaryRequestBody } from 'services/recipes';
import type {
  RecipeFilterGroup,
  SummaryPreferences,
  SummaryResponse,
} from 'services/recipes/types';
import { useSearchParams } from 'renderer/hooks/useSearchParams';
import { queryToFilters } from 'renderer/utils/queryToFilters';

import {
  RECIPE_FEATURED_LAST_PUBLISHED,
  RECIPE_QUERY_PAGE,
  RECIPE_QUERY_SEARCH_TERM,
  RECIPE_QUERY_SORT_BY,
} from '../constants';
import mergeFilters from '../utils/mergeFilters';
import { recipesSummaryCacheKey } from '../utils/recipesSummaryCacheKey';
import RecipeListingPageLayout from '../RecipeListingPageLayout';
import RecipeList from '../RecipeList';
import RecipeSortBy from '../RecipeSortBy';
import RecipeFilters from '../RecipeFilters';

const fallbackData: SummaryResponse = { recipes: [], filters: [], totalMatches: 0 };

type RecipeListingPageProps = {
  dietaryFilters: RecipeFilterGroup[];
  disableFilters: boolean;
  disableSortBy: boolean;
  gridSubtitle: string;
  gridTitle: string;
  gridType: 'recipe-cards' | 'featured';
  defaultSortBy: typeof RECIPE_FEATURED_LAST_PUBLISHED;
  preloadImages: boolean;
};

const RecipeListingPage = (props: RecipeListingPageProps) => {
  const [searchParams] = useSearchParams();

  const searchTerm = searchParams.get(RECIPE_QUERY_SEARCH_TERM) ?? '';

  const [mergedFilters, setMergedFilters] = useState({
    searchTerm,
    filters: fallbackData.filters,
  });

  const sortBy = searchParams.get(RECIPE_QUERY_SORT_BY) ?? '';
  const pageNumber = Number(searchParams.get(RECIPE_QUERY_PAGE) ?? 1) || 1;

  const filtersParam = queryToFilters(searchParams);

  const fetchRecipesSummary = (): Promise<SummaryResponse> => {
    const size = defaultSummaryRequestBody.preferences.size;
    const start = (pageNumber - 1) * size;

    const preferences: SummaryPreferences = {
      ...defaultSummaryRequestBody.preferences,
      start,
      ...(sortBy && { sortBy }),
    };

    return recipesService.postSummary({
      body: {
        ...(searchTerm && { search: { term: searchTerm, tags: [[]] } }),
        ...(filtersParam.length && { filters: filtersParam }),
        preferences,
      },
    });
  };

  const { data, error } = useSWR(
    recipesSummaryCacheKey(searchTerm, sortBy, pageNumber, filtersParam),
    fetchRecipesSummary,
    {
      fallbackData,
      onSuccess: response => {
        setMergedFilters(prev => {
          const isNewSearchTerm = prev.searchTerm !== searchTerm;

          return {
            searchTerm,
            filters: isNewSearchTerm
              ? response.filters
              : mergeFilters(prev.filters, response.filters),
          };
        });
      },
    },
  );

  const { filters } = mergedFilters;
  const { recipes, totalMatches } = data;

  return (
    <RecipeListingPageLayout
      error={error}
      gridType={props.gridType}
      disableFilters={props.disableFilters}
      disableSortBy={props.disableSortBy}
      gridTitle={props.gridTitle}
      gridSubtitle={props.gridSubtitle}
      preloadImages={props.preloadImages}
      filterComponent={<RecipeFilters filters={filters} />}
      sortByComponent={<RecipeSortBy />}
      recipeCardGrid={<RecipeList recipes={recipes} totalMatches={totalMatches} />}
    />
  );
};

export default RecipeListingPage;
